import { useTranslation } from "react-i18next";
import React from "react";

import classNames from "../../utils/classnames";
import { IconTrash } from "../../icons";
import { useSortableItem, DropIndicator, DragHandle } from "../../utils/PragmaticDragAndDrop";
import { StatusAppearancePicker, STATUS_APPEARANCES } from "../StatusAppearancePicker";
import { createTestId } from "../../TestableElement";
import { StatusSelector } from "../../StatusSelector";
import { generateStatusValueFromLabel, getAppearanceFromStatus } from "../utils";

export type StatusRowProps = {
  status: StatusSelector.StatusOption;
  isLabelInvalid: boolean;
  onUpdate: (id: string, updates: Partial<StatusSelector.StatusOption>) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  index: number;
};

export function StatusRow({ status, isLabelInvalid, onUpdate, onRemove, canRemove, index }: StatusRowProps) {
  const { t } = useTranslation();
  const { ref, dragHandleRef, isDragging, closestEdge } = useSortableItem({
    itemId: status.id,
    index: status.index,
  });

  return (
    <div className="relative">
      {closestEdge === "top" && <DropIndicator edge="top" />}
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={classNames("flex items-center gap-2 group", isDragging && "opacity-50")}
      >
        <div ref={dragHandleRef as React.RefObject<HTMLDivElement>} className="flex items-center">
          <DragHandle isDragging={isDragging} className="opacity-100" />
        </div>
        <StatusAppearancePicker
          value={getAppearanceFromStatus(status)}
          onChange={(appearance) => {
            const preset = STATUS_APPEARANCES[appearance];
            onUpdate(status.id, { color: preset.color, icon: preset.icon });
          }}
          testId={createTestId("status-color-picker", index.toString())}
        />
        <input
          value={status.label}
          onChange={(event) => onUpdate(status.id, { label: event.target.value })}
          placeholder={t("Status label")}
          className={classNames(
            "flex-1 rounded-md border px-3 py-2 text-sm bg-surface-base text-content-base transition focus:outline-none focus:ring-2 focus:ring-brand-1",
            isLabelInvalid ? "border-rose-300 focus:ring-rose-400" : "border-stroke-base",
          )}
          data-test-id={createTestId("status-label-input", index.toString())}
        />
        <button
          type="button"
          onClick={() => onRemove(status.id)}
          className={classNames(
            "p-1 rounded transition",
            !canRemove
              ? "text-content-subtle cursor-not-allowed opacity-50"
              : "text-content-dimmed hover:text-red-500 hover:bg-red-50",
          )}
          aria-label={t("Remove status")}
          disabled={!canRemove}
          data-test-id={createTestId("remove-status", index.toString())}
        >
          <IconTrash size={16} />
        </button>
      </div>
      {closestEdge === "bottom" && <DropIndicator edge="bottom" />}
    </div>
  );
}

export function applyStatusUpdate(
  status: StatusSelector.StatusOption,
  updates: Partial<StatusSelector.StatusOption>,
): StatusSelector.StatusOption {
  const updatedStatus = { ...status, ...updates };

  if (updates.label !== undefined) {
    const previousLabelSlug = generateStatusValueFromLabel(status.label ?? "");
    const isAutoGenerated = !status.value || status.value === previousLabelSlug;

    if (isAutoGenerated) {
      updatedStatus.value = generateStatusValueFromLabel(updates.label);
    }
  }

  if (updates.color !== undefined || updates.icon !== undefined) {
    const newAppearance = getAppearanceFromStatus(updatedStatus);
    updatedStatus.closed = newAppearance === "green" || newAppearance === "red";
  }

  return updatedStatus;
}
